// Do not delete this component, it's used by starter packs
// If changed, let the starter pack team know
import * as React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"

import { AppStore } from "../../store"
import OldUIContext from "../../olduicontext"
import { IOldUIContext } from "../../global"

import * as AISlice from "./slice"
import { IInputButton, IMessage } from "."
import ActionHandlerRegistry from "./utils/ActionHandlerRegistry"
import RootRegistry from "./utils/RootRegistry"

import AIModal, { Props as AIModalProps } from "./components/AIModal"

interface OldUIInit {
    start: (elementSelector: string, props: StartProps) => void
    addMessage: (id: string, message: IMessage, onClick?: () => void) => void
    setIsLoading: (id: string, isLoading: boolean) => void
    setIsVisible: (id: string, isVisible: boolean) => void
}

interface IInputButtonWithAction extends IInputButton {
    onClick: () => void
}

interface StartProps extends AIModalProps {
    inputButtonsWithAction?: IInputButtonWithAction[]
    messages?: IMessage[]
}

function init(store: AppStore, oldUIContext: IOldUIContext): OldUIInit {
    const start = (
        elementSelector: string,
        { id, inputButtonsWithAction, messages, ...modalProps }: StartProps,
    ): void => {
        // we cannot pass down an element, because this is called from an iframe custom view
        // but the container we should load into is not within an iframe
        const element = document.querySelector(elementSelector)
        if (!element) {
            // target container doesn't exist, skip initialization
            return
        }

        // for each button we need to register the onclick actions
        // as we cannot save them in the state
        const inputButtons: IInputButton[] = []
        if (!!inputButtonsWithAction) {
            inputButtonsWithAction.forEach(({ onClick, ...rest }) => {
                inputButtons.push(rest)
                ActionHandlerRegistry.add(rest.id, onClick)
            })
        }

        store.dispatch(
            AISlice.initialize({
                id,
                inputButtons,
                messages: messages ?? [],
            }),
        )

        const root = createRoot(element)
        RootRegistry.add(id, root)

        root.render(
            <Provider store={store}>
                <OldUIContext.Provider value={oldUIContext}>
                    <AIModal id={id} {...modalProps} />
                </OldUIContext.Provider>
            </Provider>,
        )
    }

    const addMessage = (
        id: string,
        message: IMessage,
        onClick?: () => void,
    ): void => {
        store.dispatch(
            AISlice.addMessage({
                id,
                message,
            }),
        )

        if (!!onClick && message.type === "Action") {
            ActionHandlerRegistry.add(message.id, onClick)
        }
    }

    const setIsLoading = (id: string, value: boolean): void => {
        store.dispatch(AISlice.setIsLoading({ id, value }))
    }

    const setIsVisible = (id: string, value: boolean): void => {
        store.dispatch(AISlice.setIsVisible({ id, value }))
    }

    return {
        start,
        addMessage,
        setIsLoading,
        setIsVisible,
    }
}

export default init
